import { useState } from 'react'
import { Trans } from 'react-i18next'

import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { generateColorTokensV2, invertHexColor, MaterialIcon, BasePopover as Popover } from '@makyoapp/makyo-tw-ui'
import { ColorPickerV2, IColor } from '@makyoapp/makyo-ui'

import { cn } from '@/lib/cn'
import { useOrgPerm } from '@/hooks/useOrgPerm'
import { ExtendedColor, sourceToHex } from '@/shared/brand-kit'
import { MAX_EXTENDED_COLORS } from '@/shared/constants'
import { useDebounceInput } from '@/shared/makyo-hooks'

interface ExtendedColorPaletteProps {
  isAdd?: boolean
  extended: ExtendedColor
  onSave: (color: IColor) => Promise<void>
  onRemove?: () => Promise<void>
}

const ExtendedColorPalette = ({ extended, onSave, isAdd, onRemove }: ExtendedColorPaletteProps) => {
  const [isEdited, setIsEdited] = useState(false)
  const [hex, setHex] = useState(extended.hex)

  const handleCancel = () => {
    setHex(extended.hex)
    setIsEdited(false)
  }

  const handleSave = async (color: IColor) => {
    await onSave(color)
    setIsEdited(false)
  }

  return (
    <ColorPickerV2
      color={hex}
      onChange={(color) => setHex(color.hex)}
      onCancel={handleCancel}
      onSave={handleSave}
      renderToggle={() =>
        isAdd ? (
          <div
            title='Add Color'
            key={extended.label}
            role='button'
            className={cn(
              'cursor-pointer h-[90px] w-full overflow-hidden truncate rounded-md mb-1 p-2 text-center border flex flex-col items-center',
              isEdited ? 'bdr justify-end ' : 'mk-bdr-hover justify-center '
            )}
            style={isEdited ? { backgroundColor: hex, color: invertHexColor(hex) } : {}}
            onClick={() => {
              setIsEdited(true)
            }}
          >
            {isEdited ? (
              <div className='shrink-0'>{hex.toUpperCase()}</div>
            ) : (
              <>
                <div className='flex-1 flex items-center justify-center'>
                  <MaterialIcon icon='add' size={28} />
                </div>

                <div className='shrink-0'>
                  <div className='flex items-center justify-center gap-1'>
                    <div className='text-m-m'>Add Color</div>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div
            key={extended.label}
            role='button'
            className={cn(
              'group relative cursor-pointer h-[90px] w-full truncate rounded-md mb-1 p-2 text-center flex flex-col items-center justify-end border bdr',
              'hover:opacity-90 hover:border-gray-500 dark:hover:border-gray-400'
            )}
            style={{ backgroundColor: hex, color: invertHexColor(hex) }}
          >
            <div className='flex flex-1 items-center justify-center'>
              <MaterialIcon icon='colorize' size={20} />
            </div>

            <div className='shrink-0'>
              <div className={cn('shrink-0 text-m-m')}>{hex.toUpperCase()}</div>
            </div>

            {onRemove && (
              <div
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
                className='absolute top-0 right-0 p-1 size-4.5 rounded-lg flex-cc bg-red-500 text-white'
              >
                <MaterialIcon icon='close' size={16} />
              </div>
            )}
          </div>
        )
      }
    />
  )
}

const ColorPalette = ({
  title,
  palette,
  sourceTone,
}: {
  title: string
  palette: Record<number, [string, string]>
  sourceTone?: number
}) => {
  const [copiedHex, setCopiedHex] = useState<string | null>(null)

  const handleCopy = async (hex: string) => {
    await navigator.clipboard.writeText(hex)
    setCopiedHex(hex)
    setTimeout(() => {
      setCopiedHex(null)
    }, 1500)
  }

  return (
    <div className='relative'>
      <h3 className='font-semibold mb-3'>
        <Trans>{title} Palette</Trans>
      </h3>
      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-13 gap-1'>
        {Object.entries(palette).map(([tone, hex]) => {
          const isSourceColor = Number(tone) === sourceTone
          const [color, contrastColor] = hex

          return (
            <Popover key={`${title}-${tone}`} modal={true}>
              <Popover.Trigger>
                <div
                  role='button'
                  className='group cursor-pointer h-[90px] w-full rounded-md mb-1 p-2 text-center flex flex-col items-center justify-between border border-gray-200 dark:border-gray-700 hover:opacity-90 hover:border-gray-500 dark:hover:border-gray-400'
                  style={{ backgroundColor: color, color: contrastColor }}
                >
                  <div className='flex-1 flex items-center justify-center'>
                    {isSourceColor ? (
                      <MaterialIcon icon='lock' size={20} />
                    ) : (
                      <div className='hidden group-hover:block'>
                        <MaterialIcon icon='frame_inspect' size={20} />
                      </div>
                    )}
                  </div>

                  <div className='shrink-0'>
                    <div className='font-medium'>{tone}</div>
                    <div className='flex items-center justify-center gap-1'>
                      <div className='text-m-m'>{color.toUpperCase()}</div>
                    </div>
                  </div>
                </div>
              </Popover.Trigger>

              <Popover.Content side='top' className='z-[9999] w-48 p-3 border bdr rounded mk-bg'>
                <div className='space-y-2'>
                  <div className='text-m-m font-medium'>
                    {title} {tone}
                  </div>
                  <div className='space-y-1 mt-2 pt-2 border-t bdr'>
                    <p className='text-m-sm text-light'>Background Color:</p>
                    <div className='flex items-center'>
                      <div className='w-5 h-5 mr-2 rounded border' style={{ backgroundColor: color }}></div>
                      <div className='flex items-center justify-between w-full'>
                        <span className='text-m-m font-mono'>{color}</span>
                        <MaterialIcon
                          icon={copiedHex === color ? 'check' : 'content_copy'}
                          size={16}
                          className={cn(
                            'ml-1 cursor-pointer',
                            copiedHex === color && 'text-green-600 dark:text-green-400'
                          )}
                          onClick={() => handleCopy(color)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='space-y-1'>
                    <p className='text-m-sm text-light'>Contrast Color:</p>
                    <div className='flex items-center'>
                      <div className='w-5 h-5 mr-2 rounded border' style={{ backgroundColor: contrastColor }}></div>
                      <div className='flex items-center justify-between w-full'>
                        <span className='text-m-m font-mono'>{contrastColor}</span>
                        <MaterialIcon
                          icon={copiedHex === contrastColor ? 'check' : 'content_copy'}
                          size={16}
                          className={cn(
                            'ml-1 cursor-pointer',
                            copiedHex === contrastColor && 'text-green-600 dark:text-green-400'
                          )}
                          onClick={() => handleCopy(contrastColor)}
                        />
                      </div>
                    </div>
                  </div>
                  {isSourceColor && (
                    <div className='flex items-center mt-2 pt-2 border-t bdr'>
                      <MaterialIcon icon='lock' size={14} className='mr-1' />
                      <p className='text-xs text-(--color-on-surface)/70'>Source color</p>
                    </div>
                  )}
                </div>
              </Popover.Content>
            </Popover>
          )
        })}
      </div>
    </div>
  )
}

interface ColorTokensDemoProps {
  source: number
  extendedColors?: ExtendedColor[]
  mode: 'dark' | 'light'
  onUpdateSourceColor: (newColor: string | IColor) => Promise<void>
  onUpdateExtendedColor: (newColor: string | IColor, index: number) => Promise<void>
  onRemoveExtendedColor?: (index: number) => Promise<void>
  isLoading?: boolean
}

export function ColorTokensDemo({
  source,
  extendedColors = [],
  mode,
  onUpdateSourceColor,
  onUpdateExtendedColor,
  onRemoveExtendedColor,
  isLoading,
}: ColorTokensDemoProps) {
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)

  const initialSourceColor = sourceToHex(source)
  const [debSourceColor, _cancel, _setDebounce, sourceColor, setSourceColor] = useDebounceInput(initialSourceColor)

  const { palettes, sourceInfo } = generateColorTokensV2(debSourceColor, mode)

  return (
    <div className='h-full relative flex flex-col gap-6'>
      <section>
        <h2 className='text-m-l font-semibold mb-2'>
          <Trans>Source Color</Trans>
        </h2>

        {!canEditBrandKit ? (
          <div className='flex items-center gap-2'>
            <div className='w-8 h-8 rounded-full' style={{ backgroundColor: sourceColor }} />
            <div className='text-sm font-mono flex gap-2'>
              <span className='bg-[var(--colorsurface-variant)] px-1 py-0.5 rounded-md'>{sourceColor}</span>
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <label htmlFor='colorPicker' className='text-sm'>
              <Trans>Try a different color:</Trans>
            </label>

            <ColorPickerV2
              color={sourceColor}
              onChange={(color) => setSourceColor(color.hex)}
              onCancel={() => setSourceColor(initialSourceColor)}
              onSave={onUpdateSourceColor}
              disableAlpha
              modal
            />
          </div>
        )}
      </section>

      <hr className='bdr' />

      <section>
        <header className='mb-8 flex'>
          <div className='mr-4'>
            <MaterialIcon icon='palette' size={40} />
          </div>
          <div>
            <h2 className='text-m-l font-semibold'>
              <Trans>Color Palettes</Trans>
            </h2>
            <p className='mt-1 text-[var(--color-on-surface)]/70'>
              <Trans>
                The full tonal palettes generated from the source color. Click on each item below to see the details.
              </Trans>
            </p>
          </div>
        </header>

        <div className='space-y-6'>
          <ColorPalette title='Primary' palette={palettes.primary} sourceTone={sourceInfo.primary.tone} />
          <ColorPalette title='Secondary' palette={palettes.secondary} sourceTone={sourceInfo.secondary.tone} />
          <ColorPalette title='Tertiary' palette={palettes.tertiary} sourceTone={sourceInfo.tertiary.tone} />
          <ColorPalette title='Neutral' palette={palettes.neutral} sourceTone={sourceInfo.neutral.tone} />
        </div>

        {extendedColors.length > 0 && (
          <>
            <hr className='hr my-4' />

            <div>
              <div className='mb-3'>
                <h3 className='font-semibold'>
                  <Trans>Extended Colors</Trans>
                </h3>
                <p className='text-m-sm text-light'>Click on each item below to change the color</p>
              </div>

              <div
                className='grid gap-1 max-w-full overflow-hidden'
                style={{
                  gridTemplateColumns: `repeat(${Math.min(MAX_EXTENDED_COLORS, 12)}, 1fr)`,
                }}
              >
                {extendedColors.map((c, idx) => (
                  <ExtendedColorPalette
                    key={idx}
                    extended={c}
                    onSave={async (color) => {
                      await onUpdateExtendedColor(color, idx)
                    }}
                    // onRemove={async () => {
                    //   await onRemoveExtendedColor(idx)
                    // }}
                  />
                ))}

                {extendedColors.length < MAX_EXTENDED_COLORS && (
                  <ExtendedColorPalette
                    isAdd
                    extended={{ label: 'Add Color', hex: '#ffffff', argb: 0 }}
                    onSave={async (color) => {
                      await onUpdateExtendedColor(color, extendedColors.length)
                    }}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
